'use client';

import Link from 'next/link';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function PortingGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs &amp; Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Porting &amp; Hosting a Phone Number</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">

          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">Overview</a>
                <a href="#porting-vs-hosting" className="block text-sm text-gray-600 hover:text-brand transition-colors">Porting vs. Hosting</a>
                <a href="#requirements" className="block text-sm text-gray-600 hover:text-brand transition-colors">Requirements</a>
                <a href="#hosting-process" className="block text-sm text-gray-600 hover:text-brand transition-colors">Hosting Process</a>
                <a href="#porting-process" className="block text-sm text-gray-600 hover:text-brand transition-colors">Porting Process</a>
                <a href="#timeline" className="block text-sm text-gray-600 hover:text-brand transition-colors">Timeline</a>
                <a href="#faq" className="block text-sm text-gray-600 hover:text-brand transition-colors">FAQ</a>
                <a href="#support" className="block text-sm text-gray-600 hover:text-brand transition-colors">Get Help</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Porting &amp; Hosting a Phone Number</h1>
              <p className="text-xl text-gray-600">
                Use your existing business phone number with Voley for SMS, or transfer it entirely. This guide covers both options.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 8 minutes</span>
              </div>
            </div>

            <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-8 [&>:first-child]:mt-0">
              <p className="text-sm text-gray-700 mb-0">
                <strong>Prerequisites:</strong> You must have completed <Link href="/docs/compliance" className="text-brand hover:text-[#2a6bb0]">10DLC registration</Link> before porting or hosting a number. If you haven&apos;t registered yet, start there first.
              </p>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">

              <section id="overview" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Overview</h2>
                <p className="text-gray-600 mb-4">
                  Many businesses already have a phone number their clients recognize. Rather than asking clients to save a new number, Voley lets you use your existing number for text messaging.
                </p>
                <p className="text-gray-600">
                  There are two ways to bring your number to Voley: <strong>hosting</strong> (recommended for most agencies) and <strong>porting</strong>. The right option depends on whether you still need the number for voice calls with your current carrier.
                </p>
              </section>

              <section id="porting-vs-hosting" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Porting vs. Hosting</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 not-prose">
                  <div className="border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                        <svg className="h-4 w-4 text-[#4a9a6a]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">Hosting (Recommended)</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>Keep your current voice carrier</li>
                      <li>Only SMS/MMS routes through Voley</li>
                      <li>No disruption to existing phone service</li>
                      <li>Can be reversed at any time</li>
                      <li>5&ndash;10 business days</li>
                    </ul>
                  </div>
                  <div className="border border-gray-200 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center">
                        <svg className="h-4 w-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">Full Porting</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>Transfers the number entirely to Voley</li>
                      <li>Voice and SMS handled by Voley&apos;s carrier</li>
                      <li>Your old carrier releases the number</li>
                      <li>Best when you no longer need voice on that number</li>
                      <li>7&ndash;14 business days</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>For most insurance agencies:</strong> We recommend hosting. It lets you keep your current phone system for voice calls while adding Voley for texting. There&apos;s no disruption to your day-to-day operations.
                  </p>
                </div>
              </section>

              <section id="requirements" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Requirements</h2>
                <p className="text-gray-600 mb-4">Before starting, make sure you have:</p>
                <div className="not-prose space-y-3 mb-6">
                  {[
                    { label: 'Completed 10DLC Registration', desc: 'Brand and campaign must be approved' },
                    { label: 'Business Phone Number', desc: 'Landline, VoIP, or toll-free — mobile numbers cannot be hosted or ported' },
                    { label: 'Current Carrier Information', desc: 'Know who provides your phone service (AT&T, Verizon, RingCentral, etc.)' },
                    { label: 'Authority to Make Changes', desc: 'You must be authorized to modify the business phone account' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100">
                      <svg className="h-5 w-5 text-brand-green mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="hosting-process" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Hosting Process (Step-by-Step)</h2>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 1: Check Number Eligibility</h3>
                <p className="text-gray-600 mb-4">
                  Navigate to <strong>Settings &rarr; Phone Number &rarr; Register Number</strong> and select &quot;Host Existing Number.&quot; Enter your business phone number and click <strong>Check Eligibility</strong>.
                </p>
                <p className="text-gray-600 mb-4">The system verifies your number type, current carrier, and SMS hosting capability. If eligible, you&apos;ll see a confirmation and can proceed.</p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 2: Submit Hosting Request</h3>
                <p className="text-gray-600 mb-4">Provide the following information:</p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-1">
                  <li>Business legal name (must match your 10DLC registration)</li>
                  <li>Current carrier name</li>
                  <li>Authorized contact person and email</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 3: Verify Phone Number Ownership</h3>
                <p className="text-gray-600 mb-4">
                  Within minutes of submitting, you&apos;ll receive a phone call to your business number with a 6-digit verification code. Enter the code in Voley to confirm ownership.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Important:</strong> The verification code expires in 10 minutes. If you miss the call, you can request another one from the status page.
                  </p>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 4: Sign the Letter of Authorization (LOA)</h3>
                <p className="text-gray-600 mb-4">
                  After verification, you&apos;ll receive an email with a Letter of Authorization to sign electronically. The LOA authorizes the SMS routing change only — your voice service stays exactly the same.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 5: Carrier Processing</h3>
                <p className="text-gray-600 mb-4">
                  Once the LOA is signed, carriers process the technical changes. This typically takes 3&ndash;7 business days. You can check the status anytime in your Phone Number settings.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Step 6: Activation</h3>
                <p className="text-gray-600 mb-4">
                  When processing is complete, your number is active for SMS through Voley. We recommend sending a test message to confirm everything is working.
                </p>
              </section>

              <section id="porting-process" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Full Porting Process</h2>
                <p className="text-gray-600 mb-4">
                  Full porting transfers your number entirely from your current carrier to Voley&apos;s carrier (Twilio). This is a more involved process and is best suited for numbers you no longer need for voice calls.
                </p>
                <p className="text-gray-600 mb-4">
                  Because full porting affects your voice service, <strong>we handle this as an assisted process</strong>. Contact our support team to get started:
                </p>
                <div className="not-prose bg-gray-50 rounded-2xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                      <svg className="h-4 w-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Contact support to start a port</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Email <a href="mailto:support@voley.us" className="text-brand hover:text-[#2a6bb0]">support@voley.us</a> with your business name, the number you want to port, and your current carrier. We&apos;ll walk you through the rest.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600">
                  The porting team will check portability, generate the required Letter of Authorization, coordinate with the losing carrier, and keep you updated on progress. The typical timeline is 7&ndash;14 business days.
                </p>
              </section>

              <section id="timeline" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Expected Timeline</h2>
                <div className="not-prose">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 pr-4 font-semibold text-gray-900">Step</th>
                        <th className="text-left py-3 pr-4 font-semibold text-gray-900">Hosting</th>
                        <th className="text-left py-3 font-semibold text-gray-900">Full Port</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 pr-4">Eligibility check</td>
                        <td className="py-2.5 pr-4">Instant</td>
                        <td className="py-2.5">1&ndash;2 business days</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 pr-4">Submit request</td>
                        <td className="py-2.5 pr-4">10 minutes</td>
                        <td className="py-2.5">Assisted by support</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 pr-4">Verification &amp; LOA</td>
                        <td className="py-2.5 pr-4">1&ndash;2 days</td>
                        <td className="py-2.5">1&ndash;3 days</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 pr-4">Carrier processing</td>
                        <td className="py-2.5 pr-4">3&ndash;7 business days</td>
                        <td className="py-2.5">5&ndash;10 business days</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 pr-4 font-medium text-gray-900">Total</td>
                        <td className="py-2.5 pr-4 font-medium text-gray-900">5&ndash;10 business days</td>
                        <td className="py-2.5 font-medium text-gray-900">7&ndash;14 business days</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="faq" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Frequently Asked Questions</h2>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Will hosting affect my voice calls?</h3>
                <p className="text-gray-600 mb-4">
                  No. Hosting only routes SMS/MMS through Voley. Your inbound and outbound voice calls continue to work exactly as before through your current carrier.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Can I host a mobile number?</h3>
                <p className="text-gray-600 mb-4">
                  No. Mobile numbers cannot be hosted because carriers don&apos;t allow split routing of SMS on mobile lines. You can host landline, VoIP, or toll-free numbers.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">What if I want to go back to my old setup?</h3>
                <p className="text-gray-600 mb-4">
                  Hosting is reversible. Contact our support team and we&apos;ll remove the SMS routing so everything goes back through your original carrier. Full ports are more complex to reverse and require porting the number back to your original carrier.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Is there an extra cost?</h3>
                <p className="text-gray-600 mb-4">
                  No additional charge from Voley for hosting or porting. Standard messaging rates apply once the number is active.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Which providers are supported for hosting?</h3>
                <p className="text-gray-600 mb-4">
                  Voley supports hosting from most major providers, including RingCentral, Lightspeed, HawkSoft, and many others. If you&apos;re unsure, the eligibility check will confirm compatibility.
                </p>
              </section>

              <section id="support" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Get Help</h2>
                <div className="not-prose bg-white rounded-2xl px-5 py-4 border border-gray-200">
                  <p className="text-gray-600 mb-4">
                    Have questions about porting or hosting? Our team is here to help.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="mailto:support@voley.us"
                      className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-white no-underline"
                      style={{ backgroundColor: BRAND_BLUE }}
                    >
                      Email support@voley.us
                    </a>
                    <Link
                      href="/contact"
                      className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 no-underline"
                    >
                      Contact us
                    </Link>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
